import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { ContentProvider } from "@/context/ContentContext";
import Layout from "@/components/Layout";
import AnalyticsTracker from "@/lib/analytics";
import Home from "@/pages/Home";
import Destinations from "@/pages/Destinations";
import DestinationDetail from "@/pages/DestinationDetail";
import Packages from "@/pages/Packages";
import PackageDetail from "@/pages/PackageDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AdminLeads from "@/pages/AdminLeads";

// Lazy-loaded content-heavy routes (keeps the main bundle small; article bodies
// in blogData.js load only when an article page is opened).
const Journal = lazy(() => import("@/pages/Journal"));
const JournalArticle = lazy(() => import("@/pages/JournalArticle"));
const ToursHub = lazy(() => import("@/pages/ToursHub"));
const SeoLanding = lazy(() => import("@/pages/SeoLanding"));
const Explore = lazy(() => import("@/pages/Explore"));
const Partners = lazy(() => import("@/pages/Partners"));
const DmcLanding = lazy(() => import("@/pages/DmcLanding"));
const StateHub = lazy(() => import("@/pages/StateHub"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const TransferLanding = lazy(() => import("@/pages/TransferLanding"));

const PageFallback = () => (
  <div className="min-h-screen grid place-items-center pt-32 text-[var(--tx-text-2)]">Loading…</div>
);

function App() {
  return (
    <div className="App">
      <ContentProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/destinations" element={<Destinations />} />
                <Route path="/destinations/:slug" element={<DestinationDetail />} />
                <Route path="/packages" element={<Packages />} />
                <Route path="/packages/:slug" element={<PackageDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/journal/:slug" element={<JournalArticle />} />
                <Route path="/tours" element={<ToursHub />} />
                <Route path="/tours/:slug" element={<SeoLanding />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/northeast-dmc" element={<DmcLanding regionKey="northeast" />} />
                <Route path="/sikkim-dmc" element={<DmcLanding regionKey="sikkim" />} />
                <Route path="/meghalaya-dmc" element={<DmcLanding regionKey="meghalaya" />} />
                <Route path="/arunachal-pradesh-dmc" element={<DmcLanding regionKey="arunachal" />} />
                <Route path="/assam-dmc" element={<DmcLanding regionKey="assam" />} />
                <Route path="/nagaland-dmc" element={<DmcLanding regionKey="nagaland" />} />
                <Route path="/bhutan-dmc" element={<DmcLanding regionKey="bhutan" />} />
                <Route path="/darjeeling-dmc" element={<DmcLanding regionKey="darjeeling" />} />
                <Route path="/dmc-in-siliguri" element={<DmcLanding regionKey="siliguri" />} />
                <Route path="/dmc-in-guwahati" element={<DmcLanding regionKey="guwahati" />} />
                <Route path="/dmc-in-gangtok" element={<DmcLanding regionKey="gangtok" />} />
                <Route path="/dmc-in-shillong" element={<DmcLanding regionKey="shillong" />} />
                <Route path="/dmc-in-kolkata" element={<DmcLanding regionKey="kolkata" />} />
                <Route path="/dmc-in-bagdogra" element={<DmcLanding regionKey="bagdogra" />} />
                <Route path="/tour-operator-in-siliguri" element={<DmcLanding regionKey="to-siliguri" />} />
                <Route path="/tour-operator-in-guwahati" element={<DmcLanding regionKey="to-guwahati" />} />
                <Route path="/tour-operator-in-gangtok" element={<DmcLanding regionKey="to-gangtok" />} />
                <Route path="/tour-operator-in-shillong" element={<DmcLanding regionKey="to-shillong" />} />
                <Route path="/tour-operator-in-kolkata" element={<DmcLanding regionKey="to-kolkata" />} />
                <Route path="/tour-operator-in-bagdogra" element={<DmcLanding regionKey="to-bagdogra" />} />
                <Route path="/sikkim-tourism" element={<StateHub stateKey="sikkim" />} />
                <Route path="/meghalaya-tourism" element={<StateHub stateKey="meghalaya" />} />
                <Route path="/assam-tourism" element={<StateHub stateKey="assam" />} />
                <Route path="/arunachal-pradesh-tourism" element={<StateHub stateKey="arunachal" />} />
                <Route path="/nagaland-tourism" element={<StateHub stateKey="nagaland" />} />
                <Route path="/bhutan-tourism" element={<StateHub stateKey="bhutan" />} />
                <Route path="/darjeeling-tourism" element={<StateHub stateKey="darjeeling" />} />
                <Route path="/airport-transfers" element={<TransferLanding hubKey="index" />} />
                <Route path="/bagdogra-airport-transfers" element={<TransferLanding hubKey="bagdogra-airport" />} />
                <Route path="/guwahati-airport-transfers" element={<TransferLanding hubKey="guwahati-airport" />} />
                <Route path="/njp-railway-station-transfers" element={<TransferLanding hubKey="njp-railway" />} />
                <Route path="/guwahati-railway-station-transfers" element={<TransferLanding hubKey="guwahati-railway" />} />
                <Route path="/siliguri-bus-stand-transfers" element={<TransferLanding hubKey="siliguri-bus-stand" />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Legal page="privacy" />} />
                <Route path="/terms" element={<Legal page="terms" />} />
                <Route path="/cancellation" element={<Legal page="cancellation" />} />
                <Route path="*" element={<NotFound />} />
                <Route path="/admin" element={<AdminLeads />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ContentProvider>
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "var(--tx-surface)",
            border: "1px solid var(--tx-border)",
            color: "var(--tx-text)",
          },
        }}
      />
    </div>
  );
}

export default App;
